import { useState, useEffect } from "react";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import {
    InputGroup,
    Label,
    SideCancelBtn,
    SubmitBtn,
    ModalHeader,
    Wrapper,
    ScrollableContent,
    ActionSection,
    FieldWrappers,
} from "../../Styles/InputStyles";
import {
    MdClose,
    MdOutlineAutorenew,
    MdKeyboardArrowRight,
    MdEdit
} from "react-icons/md";
import tw from "tailwind-styled-components";
import SelectOptionModal from "../../SelectOptionModal";

const AutoPayEnableDisable = ({
    setIsConvertAutoPay,
    selectedData,
    convertAutoPayLoading,
    convertAutoPayMutate,
    manualPayLoading,
    manualPayMutate,
}) => {
    const [selectedBeneficiaryId, setSelectedBeneficiaryId] = useState(null);
    const [isBeneficiaryModalOpen, setIsBeneficiaryModalOpen] = useState(false);
    const isAutoPayEnabled = selectedData?.isAutopay === 1;
    const beneficiaryRecords = selectedData?.beneficiaryRecords || [];
    const hasMultipleBeneficiaries = beneficiaryRecords.length > 1;
    const hasSingleBeneficiary = beneficiaryRecords.length === 1;
    const currentBeneficiaryId = selectedData?.currentBeneficiaryId;
    const currentBeneficiary = beneficiaryRecords.find(
        record => record.id === currentBeneficiaryId
    );

    useEffect(() => {
        if (hasSingleBeneficiary) {
            setSelectedBeneficiaryId(beneficiaryRecords[0]?.id);
        } else {
            setSelectedBeneficiaryId(null);
        }
    }, [hasSingleBeneficiary, beneficiaryRecords]);

    const getBeneficiaryLabel = (id) => {
        if (!id) return "Select Beneficiary";
        const beneficiary = beneficiaryRecords.find(b => b.id === id);
        return beneficiary ? `${beneficiary.name} - ${beneficiary.accountNum}` : "Select Beneficiary";
    };

    const beneficiaryOptions = beneficiaryRecords.map((record) => ({
        value: record.id,
        label: `${record.name} - ${record.accountNum}`,
    }));

    // Validation schema
    const validationSchema = Yup.object({
        beneficiaryId: Yup.number()
            .required("Please select a beneficiary")
            .nullable(),
    });

    const initialValues = {
        beneficiaryId: hasSingleBeneficiary
            ? beneficiaryRecords[0]?.id
            : null,
    };

    const handleSubmit = (values) => {
        const payload = {
            recurringExpenseId: selectedData?.id,
            beneficiaryId: values.beneficiaryId,
        };

        if (isAutoPayEnabled) {
            manualPayMutate(payload);
        } else {
            convertAutoPayMutate(payload);
        }
    };

    const handleBeneficiarySelect = (selectedValue, setFieldValue) => {
        if (selectedValue) {
            setFieldValue("beneficiaryId", selectedValue);
            setSelectedBeneficiaryId(selectedValue);
        } else {
            setFieldValue("beneficiaryId", null);
            setSelectedBeneficiaryId(null);
        }
        setIsBeneficiaryModalOpen(false);
    };

    const getTitle = () => {
        return isAutoPayEnabled ? "Disable AutoPay" : "Enable AutoPay";
    };

    const getDescription = () => {
        if (isAutoPayEnabled) {
            return "This will stop automatic payments for this recurring expense.";
        }

        return "This will automatically pay this recurring expense to the selected beneficiary.";
    };

    const getButtonText = () => {
        if (isAutoPayEnabled) {
            return manualPayLoading ? "Disabling..." : "Disable AutoPay";
        } else {
            return convertAutoPayLoading ? "Enabling..." : "Enable AutoPay";
        }
    };

    const isLoading = isAutoPayEnabled ? manualPayLoading : convertAutoPayLoading;

    return (
        <Container>
            <ModalHeader>
                <h1 className="text-white text-lg font-medium">{getTitle()}</h1>
                <button onClick={() => setIsConvertAutoPay(false)}>
                    <MdClose color="white" size={24} />
                </button>
            </ModalHeader>

            <div className="px-6 h-full flex flex-col justify-between">
                <Formik
                    initialValues={initialValues}
                    validationSchema={validationSchema}
                    onSubmit={handleSubmit}
                    enableReinitialize={true}
                >
                    {({ setFieldValue, values, errors, touched }) => (
                        <Form>
                            <ScrollableContent className="max-h-[calc(100dvh-140px)]">
                                <Wrapper>
                                    <div>
                                        <p className="text-sm text-gray-600">
                                            {getDescription()}
                                        </p>
                                    </div>

                                    {/* Status Badge */}
                                    <StatusCard $enabled={isAutoPayEnabled}>
                                        <StatusIcon $enabled={isAutoPayEnabled}>
                                            <MdOutlineAutorenew size={20} />
                                        </StatusIcon>

                                        <div className="flex flex-col">
                                            <span className="text-sm font-semibold">
                                                AutoPay {isAutoPayEnabled ? "is ON" : "is OFF"}
                                            </span>

                                            <span className="text-xs text-gray-500">
                                                {isAutoPayEnabled
                                                    ? "Recurring payments are processed automatically"
                                                    : "Recurring payments are currently manual"
                                                }
                                            </span>
                                        </div>
                                    </StatusCard>

                                    {/* Show dropdown only when autopay is disabled and there are beneficiaries */}
                                    {!isAutoPayEnabled && hasMultipleBeneficiaries && (
                                        <InputGroup>
                                            <Label>Payment Beneficiary *</Label>

                                            <BeneficiarySelector
                                                type="button"
                                                onClick={() => setIsBeneficiaryModalOpen(true)}
                                            >
                                                <div className="flex flex-col text-left min-w-0">
                                                    <span className="text-xs text-gray-400">
                                                        Pay to
                                                    </span>

                                                    <span className="text-sm text-gray-700 truncate">
                                                        {getBeneficiaryLabel(values.beneficiaryId)}
                                                    </span>
                                                </div>

                                                <MdKeyboardArrowRight
                                                    size={22}
                                                    className="text-gray-400 flex-shrink-0"
                                                />
                                            </BeneficiarySelector>

                                            {touched.beneficiaryId && errors.beneficiaryId && (
                                                <div className="text-rose-500 text-xs mt-1">
                                                    {errors.beneficiaryId}
                                                </div>
                                            )}
                                        </InputGroup>
                                    )}

                                    {/* Show beneficiary name when only one */}
                                    {!isAutoPayEnabled && hasSingleBeneficiary && (
                                        <InputGroup>
                                            <Label>Payment Beneficiary *</Label>

                                            <BeneficiarySelector
                                                type="button"
                                                onClick={() => setIsBeneficiaryModalOpen(true)}
                                            >
                                                <div className="flex flex-col text-left min-w-0">
                                                    <span className="text-xs text-gray-400">
                                                        Pay to
                                                    </span>

                                                    <span className="text-sm text-gray-700 truncate">
                                                        {getBeneficiaryLabel(values.beneficiaryId)}
                                                    </span>
                                                </div>

                                                <MdKeyboardArrowRight
                                                    size={22}
                                                    className="text-gray-400 flex-shrink-0"
                                                />
                                            </BeneficiarySelector>

                                            {touched.beneficiaryId && errors.beneficiaryId && (
                                                <div className="text-rose-500 text-xs mt-1">
                                                    {errors.beneficiaryId}
                                                </div>
                                            )}
                                        </InputGroup>
                                    )}

                                    {/* Show current beneficiary when autopay is enabled */}
                                    {isAutoPayEnabled && (
                                        <InputGroup>
                                            {/* <Label>Current Beneficiary</Label>
                                            <div className="w-full border border-gray-300 rounded-lg p-2 bg-gray-50" style={{ height: "46px", display: "flex", alignItems: "center" }}>
                                                <span className="text-gray-700">
                                                    {currentBeneficiary ? (
                                                        `${currentBeneficiary.name} - ${currentBeneficiary.accountNum}`
                                                    ) : (
                                                        selectedData?.paidToName || "Unknown Beneficiary"
                                                    )}
                                                </span>
                                            </div>
                                            <p className="text-xs text-gray-400 mt-1">
                                                Disabling AutoPay will revert to manual payments.
                                            </p> */}
                                            <Label>Payment Beneficiary</Label>

                                            <BeneficiaryDisplay>
                                                <div className="flex flex-col">
                                                    <span className="text-sm font-medium text-gray-700">
                                                        {currentBeneficiary?.name || selectedData?.paidToName || "Unknown Beneficiary"}
                                                    </span>

                                                    <span className="text-xs text-gray-500">
                                                        {currentBeneficiary?.accountNum || "--"}
                                                    </span>
                                                </div>

                                                <span className="text-xs text-green-600 font-medium">
                                                    Active
                                                </span>
                                            </BeneficiaryDisplay>
                                        </InputGroup>
                                    )}

                                    {/* No beneficiaries warning */}
                                    {!isAutoPayEnabled && beneficiaryRecords.length === 0 && (
                                        <div className="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                                            <p className="text-yellow-700 text-sm">
                                                ⚠️ No beneficiaries found. Please add a beneficiary before enabling AutoPay.
                                            </p>
                                        </div>
                                    )}
                                </Wrapper>
                            </ScrollableContent>

                            <ActionSection className="flex justify-between border-t border-gray-200 pt-4">
                                <SideCancelBtn
                                    type="button"
                                    onClick={() => setIsConvertAutoPay(false)}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </SideCancelBtn>
                                <SubmitBtn
                                    type="submit"
                                    disabled={
                                        isLoading ||
                                        (!isAutoPayEnabled && !values.beneficiaryId) ||
                                        (!isAutoPayEnabled && beneficiaryRecords.length === 0)
                                    }
                                >
                                    {getButtonText()}
                                </SubmitBtn>
                            </ActionSection>

                            {/* Beneficiary Selection Modal */}
                            <SelectOptionModal
                                isOpen={isBeneficiaryModalOpen}
                                onClose={() => setIsBeneficiaryModalOpen(false)}
                                title="Select Beneficiary"
                                options={beneficiaryOptions}
                                selectedValue={values.beneficiaryId}
                                onSelect={(value) => handleBeneficiarySelect(value, setFieldValue)}
                                searchPlaceholder="Search beneficiary..."
                            />
                        </Form>
                    )}
                </Formik>
            </div>
        </Container>
    );
};

const Container = tw.div`bg-white w-full h-full flex flex-col relative`;
const StatusCard = tw.div`
    flex items-center gap-3
    p-3
    rounded-lg
    border

    ${({ $enabled }) =>
        $enabled
            ? "bg-green-50 border-green-200 text-green-700"
            : "bg-orange-50 border-orange-200 text-orange-700"
    }
`;

const StatusIcon = tw.div`
    flex items-center justify-center
    w-9 h-9
    rounded-full

    ${({ $enabled }) =>
        $enabled
            ? "bg-green-100 text-green-600"
            : "bg-orange-100 text-orange-600"
    }
`;

const BeneficiarySelector = tw.button`
    w-full
    min-h-[54px]
    px-3
    py-2
    flex
    items-center
    justify-between
    gap-3
    rounded-lg
    border
    border-gray-300
    bg-white
    text-left
    hover:border-gray-400
    transition-colors
    cursor-pointer
`;

const BeneficiaryDisplay = tw.div`
    w-full
    min-h-[54px]
    px-3
    py-2
    rounded-lg
    border
    border-gray-200
    bg-gray-50
    flex
    items-center
    justify-between
`;

export default AutoPayEnableDisable;